export const AUTOTRANSLATOR_LIVE_TRANSCRIBE_USD_PER_MINUTE = 0.017;
export const AUTOTRANSLATOR_TRANSLATE_MODEL = "gpt-6-luna";
export const AUTOTRANSLATOR_TRANSLATE_SERVICE_TIER = "fast";
// Standard API rates for short-context requests; returned Fast/priority tiers cost 2x.
// https://developers.openai.com/api/docs/pricing
export const AUTOTRANSLATOR_PRICING_VERSION = "2026-10-12";

type OpenAITextModelPricing = {
    input: number;
    cachedInput: number;
    cacheWrite: number;
    output: number;
};

const OPENAI_TEXT_PRICING_USD_PER_MILLION: Record<string, OpenAITextModelPricing> = {
    "gpt-6-luna": {
        input: 0.1,
        cachedInput: 0.01,
        cacheWrite: 0.125,
        output: 0.5
    },
    "gpt-5.6-sol": {
        input: 4,
        cachedInput: 0.4,
        cacheWrite: 5,
        output: 20
    },
    "gpt-5.6-terra": {
        input: 2,
        cachedInput: 0.2,
        cacheWrite: 2.5,
        output: 12
    },
    "gpt-5.6-luna": {
        input: 0.2,
        cachedInput: 0.02,
        cacheWrite: 0.25,
        output: 1.2
    },
    "gpt-6.1-sol": {
        input: 2,
        cachedInput: 0.1,
        cacheWrite: 2.5,
        output: 10
    }
};

export type OpenAITextTokenUsage = {
    inputTokens: number;
    cachedInputTokens: number;
    cacheWriteTokens: number;
    outputTokens: number;
};

export type AutotranslatorTextBilling = {
    model: string;
    pricingVersion: string;
    requestCount: number;
    usageComplete: boolean;
    missingUsageRequestCount?: number;
    serviceTiers?: string[];
    textCostUsd: number;
    usage: OpenAITextTokenUsage;
};

const readNonNegativeNumber = (value: unknown): number =>
    typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : 0;

export const readOpenAITextTokenUsage = (value: unknown): OpenAITextTokenUsage | null => {
    if (!value || typeof value !== "object") {
        return null;
    }

    const usage = value as {
        input_tokens?: unknown;
        output_tokens?: unknown;
        input_tokens_details?: {
            cached_tokens?: unknown;
            cache_write_tokens?: unknown;
        } | null;
    };
    const inputTokens = readNonNegativeNumber(usage.input_tokens);
    const outputTokens = readNonNegativeNumber(usage.output_tokens);

    if (inputTokens === 0 && outputTokens === 0 &&
        !(usage.input_tokens === 0 && usage.output_tokens === 0)) {
        return null;
    }

    const cachedInputTokens = Math.min(
        inputTokens,
        readNonNegativeNumber(usage.input_tokens_details?.cached_tokens)
    );
    const cacheWriteTokens = Math.min(
        Math.max(0, inputTokens - cachedInputTokens),
        readNonNegativeNumber(usage.input_tokens_details?.cache_write_tokens)
    );

    return {
        inputTokens,
        cachedInputTokens,
        cacheWriteTokens,
        outputTokens
    };
};

export const mergeOpenAITextTokenUsage = (
    usages: readonly (OpenAITextTokenUsage | null)[]
): OpenAITextTokenUsage =>
    usages.reduce<OpenAITextTokenUsage>(
        (total, usage) => ({
            inputTokens: total.inputTokens + (usage?.inputTokens ?? 0),
            cachedInputTokens: total.cachedInputTokens + (usage?.cachedInputTokens ?? 0),
            cacheWriteTokens: total.cacheWriteTokens + (usage?.cacheWriteTokens ?? 0),
            outputTokens: total.outputTokens + (usage?.outputTokens ?? 0)
        }),
        {
            inputTokens: 0,
            cachedInputTokens: 0,
            cacheWriteTokens: 0,
            outputTokens: 0
        }
    );

const defaultTextServiceTier = (model: string): string =>
    model === AUTOTRANSLATOR_TRANSLATE_MODEL ? AUTOTRANSLATOR_TRANSLATE_SERVICE_TIER : "default";

export const calculateOpenAITextCostUsd = (
    model: string,
    usage: OpenAITextTokenUsage,
    serviceTier: unknown = defaultTextServiceTier(model)
): number | null => {
    const pricing = OPENAI_TEXT_PRICING_USD_PER_MILLION[model];
    const multiplier = serviceTier === "default" ? 1 : serviceTier === "fast" || serviceTier === "priority" ? 2 : null;

    if (!pricing || !multiplier) {
        return null;
    }

    const cachedInputTokens = Math.min(usage.inputTokens, usage.cachedInputTokens);
    const cacheWriteTokens = Math.min(
        Math.max(0, usage.inputTokens - cachedInputTokens),
        usage.cacheWriteTokens
    );
    const uncachedInputTokens = Math.max(0, usage.inputTokens - cachedInputTokens - cacheWriteTokens);

    return (
        multiplier * (uncachedInputTokens * pricing.input +
            cachedInputTokens * pricing.cachedInput +
            cacheWriteTokens * pricing.cacheWrite +
            usage.outputTokens * pricing.output) /
        1_000_000
    );
};

export const buildAutotranslatorTextBilling = (
    model: string,
    rawUsages: readonly unknown[],
    returnedServiceTiers: readonly unknown[] = []
): AutotranslatorTextBilling => {
    const parsedUsages = rawUsages.map(readOpenAITextTokenUsage);
    const usage = mergeOpenAITextTokenUsage(parsedUsages);
    // A repair can return a different tier. Price each attempt before combining.
    const serviceTiers = rawUsages.map((_usage, index) =>
        typeof returnedServiceTiers[index] === "string" && returnedServiceTiers[index]
            ? returnedServiceTiers[index] as string
            : defaultTextServiceTier(model)
    );
    const costs = parsedUsages.map((attempt, index) =>
        attempt ? calculateOpenAITextCostUsd(model, attempt, serviceTiers[index]) : null
    );

    return {
        model,
        pricingVersion: AUTOTRANSLATOR_PRICING_VERSION,
        requestCount: rawUsages.length,
        usageComplete: rawUsages.length > 0 && costs.every((value) => value !== null),
        missingUsageRequestCount: costs.filter((value) => value === null).length,
        serviceTiers,
        textCostUsd: costs.reduce<number>((total, cost) => total + (cost ?? 0), 0),
        usage
    };
};

export const estimateAutotranslatorSessionCostUsd = (elapsedMs: number, textCostUsd: number): number =>
    (Math.max(0, elapsedMs) / 60_000) * AUTOTRANSLATOR_LIVE_TRANSCRIBE_USD_PER_MINUTE +
    Math.max(0, textCostUsd);
